import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@chakra-ui/react'

import {
  Alert,
  ModalView,
  PrimaryButton,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

type CopyState = 'pending' | 'copied' | 'failed'

type CredentialFieldProps = {
  label: string
  value: string
  onCopied: (state: CopyState) => void
}

const CredentialField: React.FC<CredentialFieldProps> = ({
  label,
  value,
  onCopied,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.credential',
  })
  const onCopy = (): void => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        onCopied('copied')
      })
      .catch(() => {
        onCopied('failed')
      })
  }
  return (
    <Flex flexDirection="column" gap="1">
      <Text color="Text.midEmphasis">{label}</Text>
      <Flex alignItems="center" gap="2">
        <Box
          flex="1"
          overflowX="auto"
          padding="2"
          borderWidth="1px"
          borderColor="Border.midEmphasis"
          borderRadius="radiiMd"
          backgroundColor="Surface.secondary"
        >
          <Text fontFamily="mono" whiteSpace="nowrap">
            {value}
          </Text>
        </Box>
        <TertiaryButton onClick={onCopy}>{t('copy')}</TertiaryButton>
      </Flex>
    </Flex>
  )
}

const CopyStateText: React.FC<{ state: CopyState }> = ({ state }) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.credential',
  })
  if (state === 'copied') {
    return (
      <Text textStyle="textSm" color="Text.positiveMid">
        ✓ {t('copied')}
      </Text>
    )
  }
  return (
    <Text textStyle="textSm" color="Text.negativeMid">
      {state === 'failed' ? t('copy_failed') : t('copyWarning')}
    </Text>
  )
}

type Props = {
  open: boolean
  onClose: () => void
  keyPrefix: string
  rawKey: string
}

const CredentialModal: React.FC<Props> = ({
  open,
  onClose,
  keyPrefix,
  rawKey,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.credential',
  })
  const [copyState, setCopyState] = React.useState<CopyState>('pending')
  return (
    <ModalView
      // raw_key は再取得できないため、閉じる操作は明示的なボタンのみに制限する
      closeOnEscape={false}
      closeOnInteractOutside={false}
      displayCloseButton={false}
      bodyChildren={
        <Flex flexDirection="column" gap="4">
          <Alert status="warning">{t('warning')}</Alert>
          <CredentialField
            label={t('keyPrefix')}
            value={keyPrefix}
            onCopied={setCopyState}
          />
          <CredentialField
            label={t('apiKey')}
            value={rawKey}
            onCopied={setCopyState}
          />
        </Flex>
      }
      footerChildren={
        <Flex alignItems="center" justifyContent="space-between" gap="4">
          <CopyStateText state={copyState} />
          <PrimaryButton onClick={onClose}>{t('close')}</PrimaryButton>
        </Flex>
      }
      open={open}
      onClose={onClose}
      title={t('title')}
    />
  )
}

export default React.memo(CredentialModal)
